import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import {
  SdkStates,
  getSdkStatus,
  isSdkAuthenticatedAndUserLoaded,
  isSdkConfigured,
  isUserAuthenticated,
} from '@boldpenguin/sdk';
import { environment } from '@environments/environment';
import { Store, select } from '@ngrx/store';
import { Observable, combineLatest, race } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { BpSdkCoreService } from 'src/app/core/services/bp-sdk-core-service/bp-sdk-core.service';
import { BpSdkStoreService } from 'src/app/core/services/bp-sdk-store-service/bp-sdk-store.service';
import { selectRedirectPath } from 'src/app/core/store/redirect/redirect.selectors';
import { getCustomSubdomain } from 'src/app/shared/utilities/url-utilities';
import { getRedirectUri } from '../utilities/redirect-utilities';

/**
 * Redirects unauthenticated visitors with a custom subdomain
 * to unified auth, which will redirect them to their IDP.
 */
export const unifiedAuthGuard: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  _state: RouterStateSnapshot,
): Observable<boolean | UrlTree> => {
  const bpSdkStoreService = inject(BpSdkStoreService);
  const bpSdkCoreService = inject(BpSdkCoreService);
  const store = inject(Store);

  const authError$ = bpSdkStoreService.state$.pipe(
    filter(isSdkConfigured),
    select(getSdkStatus),
    map(
      (status) =>
        status.auth === SdkStates.Error || status.user === SdkStates.Error,
    ),
    filter((error) => error),
  );

  const authExpired$ = bpSdkStoreService.state$.pipe(
    filter(isSdkAuthenticatedAndUserLoaded),
    select(isUserAuthenticated),
    map((authenticated) => !authenticated),
  );

  return combineLatest([
    store.pipe(select(selectRedirectPath)),
    race(authError$, authExpired$),
  ]).pipe(
    map(([redirectPath, requiresAuth]) => {
      // app component hasn't loaded and set subdomain in the store yet
      const subdomain = getCustomSubdomain(
        window.location.hostname,
        window.location.search,
        environment.envTag,
      );
      const canContinue = !(subdomain && requiresAuth);
      if (!canContinue) {
        const redirectUri = getRedirectUri(redirectPath);
        bpSdkCoreService.loginUnified(redirectUri, subdomain);
      }
      return canContinue;
    }),
  );
};
